import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { presetToRange } from '../components/common/DateRangeFilter';

const STORAGE_KEY = 'endpoint_date_range';

const loadSaved = (defaultPreset) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const { key, params } = JSON.parse(raw);
      if (key && key !== 'custom') {
        return { key, params: presetToRange(key) };
      }
      if (key && params) return { key, params };
    }
  } catch { /* ignore */ }
  return { key: defaultPreset, params: presetToRange(defaultPreset) };
};

const EndpointDateRangeContext = createContext(null);

export const EndpointDateRangeProvider = ({ defaultPreset = '7d', children }) => {
  const [dateRange, setDateRange] = useState(() => loadSaved(defaultPreset).key);
  const [dateParams, setDateParams] = useState(() => loadSaved(defaultPreset).params);

  const handleDateChange = useCallback((key, range) => {
    setDateRange(key);
    setDateParams(range);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ key, params: range }));
    } catch { /* quota exceeded */ }
  }, []);

  const value = useMemo(() => ({
    dateRange,
    dateParams,
    handleDateChange,
  }), [dateRange, dateParams, handleDateChange]);

  return (
    <EndpointDateRangeContext.Provider value={value}>
      {children}
    </EndpointDateRangeContext.Provider>
  );
};

export function useEndpointDateRange() {
  const context = useContext(EndpointDateRangeContext);
  if (!context) {
    throw new Error('useEndpointDateRange must be used within an EndpointDateRangeProvider');
  }
  return context;
}

export default EndpointDateRangeContext;
